import { defineStore } from 'pinia';
import automationService from '@/services/automationService';
import axios from 'axios';

const TRIGGER_TYPES = {
  form_submission: {
    name: 'Form Submission',
    config: {
      formId: { type: 'string', required: true },
      event: { type: 'string', required: true, options: ['submission', 'step_completed'] },
    },
  },
  status_change: {
    name: 'Status Change',
    config: {
      fromStatus: { type: 'string', required: true },
      toStatus: { type: 'string', required: true },
    },
  },
  deal_status_change: {
    name: 'Deal Status Change',
    config: {
      pipelineId: { type: 'string', required: false },
      fromStageId: { type: 'string', required: false },
      toStageId: { type: 'string', required: false },
      statusType: { type: 'string', required: false, options: ['any', 'won', 'lost', 'reopened'] },
    },
  },
  scheduled: {
    name: 'Scheduled',
    config: {
      schedule: { type: 'string', required: true },
      timezone: { type: 'string', required: true },
    },
  },
};

export const useAutomationStore = defineStore('automation', {
  state: () => ({
    automations: [],
    groups: [],
    loading: false,
    error: null,
    triggerTypes: TRIGGER_TYPES,
    currentAutomation: null,
    executions: [],
    executionsPagination: null,
    executionsLoading: false,
    currentExecution: null,
  }),

  actions: {
    async fetchAutomations() {
      try {
        this.loading = true;

        const response = await axios.get(`/automations`);
        this.automations = response.data.data;
        this.groups = response.data.groups || [];
        return response.data;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async createAutomation(groupId = null) {
      try {
        this.loading = true;

        const response = await automationService.createAutomation(groupId);
        if (response.status === 201) {
          this.automations.push(response.data.data);
        }
        return response;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async fetchAutomation(id) {
      this.loading = true;
      try {
        const response = await axios.get(`/automations/${id}`);
        this.currentAutomation = response.data.data;
        return this.currentAutomation;
      } catch (error) {
        if (error.response?.status === 404) {
          this.error = 'Automation not found';
          this.currentAutomation = null;
        } else {
          this.error = error.message;
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async updateAutomation(automation) {
      try {
        this.loading = true;
        const response = await axios.patch(`/automations/${automation.id}`, automation);

        const index = this.automations.findIndex((a) => a.id === automation.id);
        if (index !== -1) {
          this.automations[index] = {
            ...this.automations[index],
            ...response.data.data,
          };
        }
        this.currentAutomation = response.data.data;
        return response;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async deleteAutomation(automationId) {
      try {
        this.loading = true;
        const response = await automationService.deleteAutomation(automationId);
        this.automations = this.automations.filter((a) => a.id !== automationId);

        if (this.currentAutomation?.id === automationId) {
          this.currentAutomation = null;
        }

        return response;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async toggleAutomation(automationId, status = null) {
      try {
        this.loading = true;
        const response = await automationService.toggleAutomation(automationId, status);
        const updatedAutomation = response?.data;

        if (updatedAutomation) {
          const index = this.automations.findIndex((a) => a.id === automationId);
          if (index !== -1) {
            this.automations[index] = {
              ...this.automations[index],
              ...updatedAutomation,
            };
          }

          if (this.currentAutomation?.id === automationId) {
            this.currentAutomation = {
              ...this.currentAutomation,
              ...updatedAutomation,
            };
          }
        }

        return response;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async createGroup(name) {
      const response = await automationService.createGroup(name);
      if (response?.data) {
        this.groups = [...this.groups, response.data];
      }
      return response.data;
    },

    async updateGroup(id, payload) {
      const response = await automationService.updateGroup(id, payload);
      if (response?.data) {
        const index = this.groups.findIndex((group) => group.id === id);
        if (index !== -1) {
          this.groups[index] = response.data;
        }
      }
      return response.data;
    },

    async deleteGroup(id) {
      await automationService.deleteGroup(id);
      this.groups = this.groups.filter((group) => group.id !== id);
      this.automations = this.automations.map((automation) =>
        automation.group_id === id ? { ...automation, group_id: null } : automation
      );
    },

    async reorder({ groups, automations }) {
      await automationService.reorder({ groups, automations });
      if (Array.isArray(groups)) {
        const order = new Map(groups.map((group) => [group.id, group.sort_order]));
        this.groups = [...this.groups]
          .map((group) =>
            order.has(group.id) ? { ...group, sort_order: order.get(group.id) } : group
          )
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
      }
      if (Array.isArray(automations)) {
        const byId = new Map(automations.map((item) => [item.id, item]));
        this.automations = this.automations.map((automation) => {
          const next = byId.get(automation.id);
          if (!next) return automation;
          return {
            ...automation,
            group_id: next.group_id,
            sort_order: next.sort_order,
          };
        });
      }
    },

    async fetchExecutions(automationId, params = {}) {
      this.executionsLoading = true;
      try {
        const response = await automationService.getExecutions(automationId, params);
        this.executions = response.data || [];
        this.executionsPagination = response.pagination || null;
        return response;
      } catch (error) {
        this.error = error.message;
        throw error;
      } finally {
        this.executionsLoading = false;
      }
    },

    async fetchExecution(automationId, executionId) {
      const response = await automationService.getExecution(automationId, executionId);
      this.currentExecution = response.data || null;
      return this.currentExecution;
    },
  },
});
